'use es6';

import tinymce from 'tinymce';
import CalloutInsertPopoverBody from 'rich-text-lib/components/callouts/CalloutInsertPopoverBody';
import CalloutPopoverManagerV2 from '../components/CalloutPopoverManagerV2';
import { CALLOUT_ATTRIBUTE, CALLOUT_ELEMENT, CALLOUT_SELECTOR, CALLOUT_TYPE_CLASS_PREFIX } from '../constants/hscallout';
import { INSERT_CALLOUT } from '../constants/usageTracking';
import { HS_CALLOUT } from '../constants/plugins';
import { CANVAS_CALLOUT } from '../constants/toolbar';
import { isNestedLastChild } from '../hsformatting/utils';
import { BOGUS_BR, isEmptyParagraph } from '../utils/dom';
import { i18nWithNameSpace } from '../utils/i18n';
import { trackInteraction } from '../utils/usageTracking';
import { getParentCallout } from '../utils/callout';
import { isNodeWithinLink } from '../utils/link';
import { isEditableImage } from '../utils/image';
import { isEditableVideo } from '../utils/video';
import { isEditableEmbed } from '../utils/embed';
import { nodeIsCtaPlaceholder } from '../utils/cta';
import { nodeIsIconElement } from '../utils/icon';
import { getSelectedCells, getSelectedTableNode } from '../utils/table';
import { getCalloutRemoveHandler, getCalloutTypeChangeHandler } from './handlers';
import { jsx as _jsx } from "react/jsx-runtime";
const i18nKey = i18nWithNameSpace('TinymcePlugins.hscallout');
const COMMON_BLOCK_ELEMENTS = ['table', 'iframe', 'video', 'pre', 'ul', 'ol', 'blockquote'];
const SPLITTABLE_BLOCK_ELEMENTS = ['p'];
function getOuterMostBlockElement(editor, element, body) {
  const blockElements = editor.dom.getParents(element, COMMON_BLOCK_ELEMENTS.concat(CALLOUT_SELECTOR).join(','), body);
  return blockElements && blockElements.length > 0 && blockElements[blockElements.length - 1];
}
function getClosestBlockElement(editor, element, body) {
  return editor.dom.getParent(element, ['p', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6'].concat(COMMON_BLOCK_ELEMENTS).concat(CALLOUT_SELECTOR).join(','), body);
}
const getCalloutBlockNode = editor => {
  const body = editor.getBody();
  let selectedNode = editor.selection.getNode();
  if (editor.selection.isCollapsed()) {
    if (selectedNode === body) {
      selectedNode = selectedNode.lastChild;
    }
  } else {
    selectedNode = editor.selection.getRng().endContainer;
  }
  const outerMostBlockElement = getOuterMostBlockElement(editor, selectedNode, body);
  const closestBlockElement = getClosestBlockElement(editor, selectedNode, body);
  return outerMostBlockElement || closestBlockElement || body.lastChild;
};
export function getInsertCalloutHandler(editor) {
  return function handleInsertCallout(type) {
    editor.undoManager.transact(() => {
      const calloutBlockNode = getCalloutBlockNode(editor);
      const newlineNode = editor.dom.create('p', {}, BOGUS_BR);
      const calloutElem = editor.dom.create(CALLOUT_ELEMENT, {
        [CALLOUT_ATTRIBUTE]: type,
        class: `${CALLOUT_TYPE_CLASS_PREFIX}-${type}`
      }, newlineNode);
      if (editor.dom.is(calloutBlockNode, SPLITTABLE_BLOCK_ELEMENTS.join(','))) {
        const bookmark = editor.selection.getBookmark();
        const bookmarkNode = editor.dom.get(`${bookmark.id}_start`);
        // When bookmark a is created in a empty <p> it will be the first child of the <p>,
        // so we need to check also for if has a nextSibling which is just a <br>
        const bookmarkNodeNextSibling = bookmarkNode.nextSibling;
        const bookmarkNodeNextSiblingIsBR = editor.dom.is(bookmarkNodeNextSibling, 'br');
        const cursorIsAtTheEnd = bookmarkNodeNextSiblingIsBR && isNestedLastChild(bookmarkNodeNextSibling, calloutBlockNode) || isNestedLastChild(bookmarkNode, calloutBlockNode);
        if (!cursorIsAtTheEnd) {
          // Clear content after bookmark
          const range = document.createRange();
          // Avoid selecting the leading <br> for getting the bottom half content
          range.setStartAfter(bookmarkNodeNextSiblingIsBR ? bookmarkNodeNextSibling : bookmarkNode);
          range.setEndAfter(calloutBlockNode.lastChild);
          editor.selection.setRng(range);
          const content = editor.selection.getContent();
          // Get the full selection in order to remove the leading <br>
          range.setStartAfter(bookmarkNode);
          editor.selection.setRng(range);
          editor.selection.setContent('');

          // Add new <p> or <blockquote> block
          const newBlock = editor.dom.create(calloutBlockNode.nodeName.toLowerCase(), {}, content);
          editor.dom.insertAfter(newBlock, calloutBlockNode);
        }
        editor.selection.moveToBookmark(bookmark);
      }
      if (isEmptyParagraph(calloutBlockNode)) {
        editor.dom.replace(calloutElem, calloutBlockNode);
      } else {
        editor.dom.insertAfter(calloutElem, calloutBlockNode);
      }
      editor.selection.setCursorLocation(newlineNode, 0);
      editor.execCommand('hsInsertNewline', false, {
        target: calloutElem
      });
    });
    trackInteraction(editor, 'tinymceInsertCallout', INSERT_CALLOUT, {
      type
    });
  };
}
export function addControls(editor) {
  const handleInsertCallout = getInsertCalloutHandler(editor);
  tinymce.canvas.addMenuItem(editor, CANVAS_CALLOUT, {
    text: i18nKey('menuItem'),
    child: () => /*#__PURE__*/_jsx(CalloutInsertPopoverBody, {
      onInsertCallout: handleInsertCallout
    })
  });
  const isActiveNodeHandler = ({
    element
  }) => {
    if (!element) return false;
    const calloutNode = getParentCallout(editor, element);
    if (!calloutNode) return false;
    const isWithinLink = isNodeWithinLink(editor, element);
    const isImage = isEditableImage(editor, element);
    const isVideo = isEditableVideo(editor, element);
    const isEmbed = isEditableEmbed(editor, element);
    const isCta = nodeIsCtaPlaceholder(editor, element);
    const isIcon = nodeIsIconElement(editor, element);
    const isTable = getSelectedTableNode(editor, element) || getSelectedCells(editor, element).length > 0;
    const elementHasOwnPopover = isWithinLink || isImage || isVideo || isEmbed || isCta || isIcon || isTable;
    return !elementHasOwnPopover && calloutNode;
  };
  tinymce.canvas.addNodeChangeHandler(editor, {
    eventKey: HS_CALLOUT,
    isActiveNodeHandler
  });
  tinymce.canvas.addPopover(editor, HS_CALLOUT, {
    renderPopover(renderProps) {
      return /*#__PURE__*/_jsx(CalloutPopoverManagerV2, Object.assign({}, renderProps, {
        editor: editor,
        eventKey: HS_CALLOUT,
        onCalloutTypeChange: getCalloutTypeChangeHandler(editor),
        onCalloutRemove: getCalloutRemoveHandler(editor)
      }));
    }
  });
}